import { useState } from 'react';
import styles from './ResultsSummary.module.css';
import ChevronDown from '../../../img/icons/chevron-down.svg';

/**
 * Build a summary of the information provided to show to users.
 *
 * @param {object} formData
 * @return {JSX}
 **/
export const ResultsSummary = ({ formData, isPrinting }) => {
  const providerType = formData.providerStep?.provider;
  const selectedDescriptionValue = formData.descriptionStep?.value;
  const selectedFunding = formData.fundingStep?.fundingSource?.filter(
    (item) => item.isChecked
  );
  const [expanded, setExpanded] = useState(false);

  const handleClickExpand = () => setExpanded(!expanded);

  let providerStatus = '';
  switch (providerType) {
    case 'prospective':
      providerStatus = 'prospective provider';
      break;
    case 'registered':
      providerStatus = 'registered provider';
      break;
    case 'other':
      providerStatus = 'other';
      break;
    default:
      providerStatus = 'unspecified';
      break;
  }

  const userIsProvider = ['prospective', 'registered'].includes(providerType);
  const userIsUnspecified = (providerStatus === 'unspecified');
  const userIsGovt = formData.govEntityStep.isGovEntity[0].isChecked;
  const selectedTopic = formData.resultsStep?.topic;
  let topicLabel = '';
  switch (selectedTopic) {
    case 'agreements':
      topicLabel = 'Agreements, plan and information';
      break;
    case 'governance':
      topicLabel = 'Governance';
      break;
    case 'reporting':
      topicLabel = 'Reporting and notifications';
      break;
    case 'workforce':
      topicLabel = 'Workforce';
      break;
    case 'complaints':
      topicLabel = 'Complaints and feedback';
      break;
    case 'all':
    default:
      topicLabel = 'All topics';
      break;
  }

  const isOtherIndividual = formData.descriptionStep?.isValid;
  let indivLabel = '';
  if (isOtherIndividual) {
    switch (formData.descriptionStep.value) {
      case 'acw':
        indivLabel = 'Aged care workers';
        break;
      case 'rp':
        indivLabel = 'Responsible persons';
        break;
      case 'dpo':
      default:
        indivLabel = 'Digital platform owners';
        break;
    }
  }

  const selectedRegistrationCategories =
        formData.servicesStep.serviceCategories.filter(
          (item) => item.selectSubCategories > 0
        ).map((item) => {
          const selectedServiceTypes = item.subCategories.filter((serviceType) => serviceType.isSelected);
          return { ...item, subCategories: selectedServiceTypes };
        });

  const simpleServicesList = selectedRegistrationCategories.map(category => {
    if (category.subCategories && !category.isSelected) return { label: category.label, hasServices: true };
    return { label: category.label };
  });

  const servicesSelectionCount = selectedRegistrationCategories.flatMap(item => {
    // Partial service type selections don't mark the category as isSelected
    if (item.isSelected && item.subCategories) return [item];
    return [...item.subCategories];
  }).length;

  const showSimple = servicesSelectionCount <= 2;

  let summaryText = <></>;
  if (userIsUnspecified) {
    summaryText = <>You are viewing the requirements under the topic of <strong>{topicLabel}</strong></>;
  } else {
    const isOther = providerStatus === 'other' && selectedDescriptionValue !== 'ap';
    summaryText = (
      <>
        Based on the information provided, you are viewing the requirements{(topicLabel === 'All topics' || !isPrinting) ? '' : <> for the topic <strong>{topicLabel.toLowerCase()}</strong></>}
        {userIsProvider
          ? (
            <>
              {!isOther ? ' of a ' : ' related to a '}<strong>{userIsGovt ? 'government entity ' : 'non-government entity '}{!['other', 'unspecified'].includes(providerStatus) && providerStatus}</strong> delivering{' '}
              {showSimple
                ? <>{simpleServicesList.map((item, index) => (
                  <span key={index}>{!!index && ' and '}<strong>{item?.hasServices && 'one or more service types from '}{item.label}</strong></span>
                ))}
                  </>
                : <><strong>multiple service types or across multiple registration categories</strong></>}
              {selectedFunding.length > 0 && <>, funded by <strong>{selectedFunding.map((item) => item.labelShort).join(', ')}</strong></>}
              .
            </>
            )
          : (
            <> related to{' '}
              {isOtherIndividual
                ? (<strong>{indivLabel}</strong>)
                : (<>
                  {simpleServicesList.length && showSimple
                    ? <>{simpleServicesList.map((item, index) => (
                      <span key={index}>{!!index && ' and '}<strong>{item?.hasServices && 'one or more service types from '}{item.label}</strong></span>
                    ))}
                    </>
                    : <><strong>multiple service types or across multiple registration categories</strong></>}
                  {selectedFunding.length > 0 && <>, funded by <strong>{selectedFunding.map((item) => item.labelShort).join(', ')}</strong></>}
                  .
                   </>)}
            </>
            )}
      </>

    );
  }

  let detailedSummaryText = null;

  if (!showSimple) {
    detailedSummaryText = (
      <ul className={styles.ul}>
        {selectedRegistrationCategories.map(category => (
          <li key={category.id}>
            {category.label}
            {category.subCategories.length > 0 &&
              <ul className={styles.ul}>
                {category.subCategories.map(serviceType => (
                  <li key={serviceType.id}>{serviceType.label}</li>
                ))}
              </ul>}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div>
      <p className={styles.summaryStatement}>
        {summaryText}

        {!showSimple && <button
          aria-controls='detailed-summary-accordion-content'
          aria-expanded={expanded}
          className={styles.detailedSummaryToggle}
          id='detailed-summary-accordion-btn'
          type='button'
          onClick={handleClickExpand}
                        >
          <span className={styles.expandBtnText}>
            {expanded ? 'Collapse details' : 'Expand details'}
          </span>
          <ChevronDown className={`${styles.expandBtnChevron} ${expanded && styles.expandBtnChevronOpen}`} aria-hidden />
                        </button>}
      </p>
      {!showSimple &&
        <div
          aria-labelledby='detailed-summary-accordion-btn'
          className={`${styles.detailedSummaryContainer} ${expanded ? styles.detailedSummaryContainerOpen : styles.detailedSummaryContainerClosed}`}
          id='detailed-summary-accordion-content'
        >
          <h3 className={styles.detailedSummaryHeading}>Registration categories and service types:</h3>
          {detailedSummaryText}
        </div>}

    </div>
  );
};

export default ResultsSummary;
